/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { Scatter } from 'react-chartjs-2';
import moment from 'moment';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend as GraphLegend
} from 'chart.js';
import { useData } from '../context/data.context';
import {
  GraphPointStyles,
  ParametersType,
  PlotTransformedDataType,
  TimeSpan
} from '../constants/telosair-interfaces';
import { getParameterByName } from '../_helpers/globalHelper';
import { COLORS, GRAPH_COLORS, POINT_STYLES } from '../constants/global-constants';
import { createColorObject, getIconByPointStyle } from '../_helpers/graph';
import useDimensions from '../hooks/useDimension';
import Loader from './loader';
import ParameterLegend from './ParameterLegend';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';

const ZOOMED_FONT_SIZE = 18;
const DEFAULT_FONT_SIZE = 15;

const axisYDefaultConfig = {
  grid: {
    display: true,
    color: GRAPH_COLORS.grid // Change the color of the x-axis lines
  }
};

/**
 * Creates an object from an array where each key is an element from the array and each value is a points style.
 * @param arr - The array of strings to be used as keys.
 * @returns An object where the keys are elements from the array and the values are pointstyles.
 */
function createPointObject(arr: number[]): { [key: string]: GraphPointStyles } {
  const pointObject: { [key: string]: GraphPointStyles } = {};

  arr.forEach((item, index) => {
    pointObject[item] = POINT_STYLES[index % POINT_STYLES.length];
  });

  return pointObject;
}

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, GraphLegend);

type Propss<T> = {
  isZoomed?: boolean;
  selectedParameters: ParametersType[];
  selectedDevices: { label: string; value: number }[];
  graphData: PlotTransformedDataType;
  timeSpan: TimeSpan;
  localParameterList: any[];
};

/**
 * React component for BoxAndWhiskerChart.
 * @param props - Properties of BoxAndWhiskerChart.
 * @returns - React component for BoxAndWhiskerChart.
 */
export default function ScatterChart<T extends string>(props: Propss<T>): ReactElement {
  const { selectedParameters, selectedDevices, graphData, timeSpan, localParameterList, isZoomed } =
    props;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const dimension = useDimensions(chartContainerRef);
  const { parameters, userConfigData } = useData();
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDevices, setActiveDevices] = useState<number[]>(() => {
    const validValues = selectedDevices.map((device) => device.value);
    const stored = (getAppliedFilter(Filters.scatterDevices)?.data as number[]) || [];
    const restored = stored.filter((value) => validValues.includes(value));
    return restored.length ? restored : validValues;
  });
  const [activeParametersYAxis, setActiveParametersYAxis] = useState<ParametersType[]>(() => {
    const stored = (getAppliedFilter(Filters.scatterParametersY)?.data as ParametersType[]) || [];
    const restored = stored.filter((value) => selectedParameters.includes(value));
    if (restored.length) return restored;
    return selectedParameters.length ? [selectedParameters[0]] : [];
  });
  const [activeParametersXAxis, setActiveParametersXAxis] = useState<ParametersType | null>(() => {
    const stored = getAppliedFilter(Filters.scatterParameterX)?.data as ParametersType | undefined;
    if (stored && selectedParameters.includes(stored)) return stored;
    return selectedParameters.length ? selectedParameters[selectedParameters.length - 1] : null;
  });

  // Persist scatter legend selections so they are retained across visits.
  useEffect(() => {
    setAppliedFilter({ key: Filters.scatterDevices, data: activeDevices });
  }, [activeDevices]);
  useEffect(() => {
    setAppliedFilter({ key: Filters.scatterParametersY, data: activeParametersYAxis });
  }, [activeParametersYAxis]);
  useEffect(() => {
    if (activeParametersXAxis) {
      setAppliedFilter({ key: Filters.scatterParameterX, data: activeParametersXAxis });
    }
  }, [activeParametersXAxis]);

  const [options, setOptions] = useState<any>({});

  useEffect(() => {
    const defaultOptions: any = {
      responsive: true,
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      hover: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: false,
          labels: {
            font: {
              size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
            }
          }
        },
        title: {
          display: false,
          font: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        },
        tooltip: {
          titleFont: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          },
          bodyFont: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          },
          callbacks: {
            // eslint-disable-next-line func-names, object-shorthand, jsdoc/require-jsdoc
            label: function (context: {
              raw: {
                label: string;
                x: string;
                y: string;
                labelX: string;
                labelY: string;
                time: string;
              };
            }) {
              const { label, labelX, labelY, x, y, time } = context.raw;
              return `${time} - ${label}: (${labelX}: ${x}, ${labelY}: ${y})`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
            // Change the color of the level labels
          },
          ticks: {
            color: GRAPH_COLORS.level,
            maxTicksLimit: 6,
            font: {
              size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
            }
          }
        }
      },
      maintainAspectRatio: false
    };
    const scales = { ...defaultOptions.scales };
    const axisIncluded: string[] = [];
    data?.datasets.forEach((item: any, index: number) => {
      if (axisIncluded.includes(item.yAxisID)) {
        return;
      }
      scales[item.yAxisID] = {
        ...axisYDefaultConfig,
        position: index === 0 ? 'left' : 'right',
        display: true,
        title: {
          text: item.name,
          display: true,
          color: item.labelColor,
          font: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        },
        ticks: {
          count: 6,
          color: item.labelColor,
          font: {
            size: isZoomed ? ZOOMED_FONT_SIZE : DEFAULT_FONT_SIZE
          }
        }
      };
      axisIncluded.push(item.yAxisID);
    });
    setOptions({
      ...defaultOptions,
      scales
    });
  }, [data, isZoomed]);

  /**
   * Function to get x axis labels.
   * @param timestamp - Timestamp of the axis label.
   * @returns - XAxis label.
   */
  const getFormattedXAxisLabel = (timestamp: number): string => {
    const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      timeSpan === TimeSpan.Week1 ||
      timeSpan === TimeSpan.Month1 ||
      timeSpan === TimeSpan.Custom
    ) {
      return moment(timestamp).tz(timeZone).format('HH:mm (Do MMM)');
    }
    return moment(timestamp).tz(timeZone).format('HH:mm');
  };

  const parameterColors = createColorObject(selectedParameters, localParameterList);
  const devicePointStyles = createPointObject(selectedDevices.map((device) => device.value));

  useEffect(() => {
    if (parameters.length > 0 && activeParametersXAxis) {
      const datasetsArray: any[] = [];
      activeParametersYAxis.forEach((yAxis, index) => {
        const labelY = getParameterByName(yAxis, parameters)?.shortName;
        const labelX = getParameterByName(activeParametersXAxis, parameters)?.shortName;
        Object.entries(graphData).forEach(([key, item]) => {
          if (activeDevices.includes(Number(key))) {
            const dataset = item.map((eachSet) => {
              const x = eachSet.parameters[activeParametersXAxis];
              const y = eachSet.parameters[yAxis];
              return {
                x,
                y,
                label: key.toString(),
                labelX: labelX || '',
                labelY: labelY || '',
                time: getFormattedXAxisLabel(eachSet.timestamp)
              };
            });
            const graphDatasetValuesPerParameter: any = {
              data: dataset,
              name: labelY,
              borderColor: parameterColors[yAxis],
              labelColor: parameterColors[yAxis],
              backgroundColor: parameterColors[yAxis],
              yAxisID: index === 0 ? 'y' : `y${index}`,
              pointStyle: devicePointStyles[key],
              pointRadius: isZoomed ? 5 : 3
            };
            datasetsArray.push(graphDatasetValuesPerParameter);
          }
        });
      });
      setData({
        datasets: datasetsArray
      });
    }
  }, [
    parameters,
    selectedParameters,
    selectedDevices,
    graphData,
    activeDevices,
    activeParametersYAxis,
    activeParametersXAxis,
    isZoomed
  ]);

  /**
   * Handles device click.
   * @param device - Clicked device.
   * @param active - Is device active.
   */
  const handleDeviceClick = (device: number, active: boolean): void => {
    if (active) {
      setActiveDevices((prev) => prev.filter((item) => item !== device));
    } else {
      setActiveDevices((prev) => [...prev, device]);
    }
  };

  /**
   * Handles device click.
   * @param parameter - Clicked parameter.
   * @param active - Is device active.
   */
  const handleParameterYAxisClick = (parameter: ParametersType, active: boolean): void => {
    if (active) {
      setActiveParametersYAxis((prev) => prev.filter((item) => item !== parameter));
    } else {
      setActiveParametersYAxis((prev) => [...prev, parameter]);
    }
  };
  /**
   * Get the CSS class for a parameter based on its active status.
   * @param  value - Value.
   * @param type - Legend type.
   * @returns  The CSS class.
   */
  const parameterClass = (value: string, type?: 'device' | 'axisY' | 'axisX'): string => {
    if (type === 'device') {
      return activeDevices.includes(Number(value)) ? 'text-white' : 'text-gray-400';
    }
    if (type === 'axisY') {
      return activeParametersYAxis.includes(value as ParametersType)
        ? 'text-white'
        : 'text-gray-400';
    }
    return activeParametersXAxis === value ? 'text-white' : 'text-gray-400';
  };

  useEffect(() => {
    let timeout: any;
    if (dimension.height) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(false);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [dimension]);

  return (
    <div ref={chartContainerRef} className="flex flex-col w-full justify-between h-full">
      <div className="flex justify-between items-center w-full gap-5">
        <div className="flex flex-wrap mt-2 gap-3">
          {selectedParameters.map((parameter) => {
            const active = activeParametersYAxis.includes(parameter);
            return (
              <ParameterLegend
                className={parameterClass(parameter, 'axisY')}
                key={parameter}
                name={getParameterByName(parameter, parameters)?.shortName || 'N/A'}
                color={parameterColors[parameter]}
                active={active}
                parameter={parameter}
                handleParameterClick={handleParameterYAxisClick}
              />
            );
          })}
        </div>
        <div className="flex flex-wrap mt-2 gap-3">
          {selectedDevices.map((device) => {
            const active = activeDevices.includes(device.value);
            return (
              <button
                key={device.value}
                type="button"
                onClick={() => handleDeviceClick(device.value, active)}
                className={`flex gap-1 items-center ${parameterClass(String(device), 'device')} ${
                  !active ? 'opacity-50' : ''
                } cursor-pointer `}
              >
                <span
                  className="material-icons text-sm"
                  style={{
                    color: '#D0D5DD'
                  }}
                >
                  {getIconByPointStyle(devicePointStyles[device.value])}
                </span>
                <div style={{ color: active ? '#fff' : '#D0D5DD' }} className="text-sm font-normal">
                  {device.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center w-full mt-4 relative">
        {loading && <Loader />}
        {!loading && (
          <div className="flex flex-1 items-end h-full">
            {data && <Scatter data={data} options={options} />}
          </div>
        )}
      </div>

      <div className="flex justify-center items-center">
        <div className="flex flex-wrap mt-2 gap-3">
          {selectedParameters.map((parameter) => {
            const active = activeParametersXAxis === parameter;
            return (
              <ParameterLegend
                className={parameterClass(parameter, 'axisX')}
                key={parameter}
                name={getParameterByName(parameter, parameters)?.shortName || 'N/A'}
                color="#D0D5DD"
                active={active}
                parameter={parameter}
                handleParameterClick={() => setActiveParametersXAxis(parameter)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
